import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Package,
  ShoppingCart,
  Search,
  Building2,
  Users,
  Settings,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
  Clipboard,
  FileText,
  Receipt,
  LayoutDashboard,
  DollarSign,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Wallet,
  Calendar,
  Store,
} from 'lucide-react';
import logo from '/vixalor-logo.webp';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  badgeColor?: 'default' | 'secondary' | 'destructive' | 'success';
}

export function SidebarNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [forceExpanded, setForceExpanded] = useState(false);
  const isCompactRoute = location.pathname === '/suppliers/invoices/new' || location.pathname === '/sales';
  const useCompactNav = isCompactRoute && !forceExpanded;

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (err: any) {
      toast.error('Failed to log out');
    }
  };

  const adminNavItems: NavItem[] = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Sales', href: '/sales', icon: ShoppingCart },
    { name: 'Debtors', href: '/debtors', icon: Calendar },
    { name: 'Inventory', href: '/inventory', icon: Package },
    { name: 'Items', href: '/items', icon: Store },
    { name: 'Suppliers', href: '/suppliers', icon: Users },
    { name: 'Expenses', href: '/expenses', icon: Receipt },
    { name: 'Accounts', href: '/accounts', icon: Wallet },
    { name: 'Reports', href: '/reports', icon: TrendingUp },
    { name: 'Branches', href: '/branches', icon: Building2 },
    { name: 'Users', href: '/users', icon: Users },
    { name: 'Activity Logs', href: '/activity-logs', icon: FileText },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const branchNavItems: NavItem[] = [
    { name: 'Sales', href: '/sales', icon: ShoppingCart },
    { name: 'Debtors', href: '/debtors', icon: Calendar },
  ];

  const navItems = isAdmin ? adminNavItems : branchNavItems;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsCollapsed(false); // Don't collapse on mobile, let AppLayout handle visibility
      } else {
        setIsCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isCompactRoute) {
      setForceExpanded(false);
    }
  }, [isCompactRoute]);

  return (
    <div
      className={cn(
        'flex flex-col h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border-r border-slate-200 dark:border-slate-700 shadow-lg transition-all duration-300',
        useCompactNav ? 'w-16' : 'w-72'
      )}
    >
      {/* Header */}
      <div className={cn('border-b border-slate-200 dark:border-slate-700', useCompactNav ? 'p-2' : 'p-6')}>
        <div className={cn('flex items-center justify-between', useCompactNav && 'justify-center')}>
          <img src={logo} alt="Vixalor" className={cn(useCompactNav ? 'h-6 w-auto' : 'h-12 w-auto')} />
          {isCompactRoute && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn('h-8 w-8 text-slate-600 hover:text-slate-900', useCompactNav && 'absolute top-2 right-2')}
              onClick={() => setForceExpanded(prev => !prev)}
              title={useCompactNav ? 'Expand navigation' : 'Collapse navigation'}
            >
              {useCompactNav ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* User Info */}
      {user && (
        <div className={cn('bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700', useCompactNav ? 'px-2 py-3' : 'px-6 py-4')}>
          <div className={cn('flex items-center', useCompactNav ? 'justify-center' : 'space-x-3')}>
            <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            {!useCompactNav && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                {user.name || 'User'}
              </p>
              <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-300 truncate">
                {user.email}
              </p>
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mt-1">
                {user.role === 'admin' ? 'Admin Access' : 'Branch Access'}
              </p>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            title={useCompactNav ? item.name : undefined}
            className={({ isActive }) =>
              cn(
                'group flex items-center rounded-xl text-sm font-medium transition-all duration-200 relative',
                useCompactNav ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3',
                isActive
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-[1.02]'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white hover:transform hover:scale-[1.01]'
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={cn(
                  "h-5 w-5 transition-colors duration-200",
                  isActive 
                    ? "text-white" 
                    : "text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200"
                )} />
                {!useCompactNav && (
                <div className="flex items-center justify-between flex-1">
                  <span className="truncate">{item.name}</span>
                  {item.badge && (
                    <Badge 
                      variant={isActive ? "secondary" : item.badgeColor || "default"}
                      className={cn(
                        "text-xs px-2 py-0.5",
                        isActive && "bg-white/20 text-white"
                      )}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </div>
                )}
                {isActive && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <Button
          variant="ghost"
          className={cn(
            'w-full text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200',
            useCompactNav ? 'justify-center px-2' : 'justify-start'
          )}
          onClick={handleLogout}
        >
          <LogOut className={cn('h-5 w-5', !useCompactNav && 'mr-3')} />
          {!useCompactNav && 'Sign Out'}
        </Button>
      </div>
    </div>
  );
}
